import React, { useEffect, useState, useRef } from "react";
import axios from "axiosClient";
import {
  Link,
  useNavigate,
  useParams,
  useLocation,
  NavLink,
} from "react-router-dom";
import {
  Row,
  Card,
  CardBody,
  Input,
  Button,
  Form,
  FormGroup,
  Label,
  Col,
  FormFeedback,
  Alert,
  Spinner,
} from "reactstrap";
import { Colxx, Separator } from "components/common/CustomBootstrap";

import AppLayout from "layout/AppLayout";
import { useToken } from "auth/useToken";
import SelectInput from "components/common/SelectInput";
import moment from "moment";
import { useUser } from "auth/useUser";
import { toast } from "react-toastify";
import { modules } from "utils/constants";
import ConfirmModal from "components/ConfirmModal";
import LoadError from "components/LoadError";
import { loadBridgeSettings, printReceipt } from "utils/posBridge";
import { buildPrintPayload } from "utils/posReceiptPrint";
import { downloadReceiptPdf } from "utils/receiptPdf";

// Printed on the receipt's candidate and exam block. The server updates the
// stored receipt when these change, so a reprint must wait for a save.
const RECEIPT_FIELDS = [
  "firstName",
  "lastName",
  "candidatePassport",
  "pinpp",
  "phoneNumber",
  "email",
  "exam",
  "module",
  "examDate",
  "enquiryId",
];

const AUDIT_LABELS = {
  created: "Created",
  updated: "Edited",
  deleted: "Deleted",
  receipt_printed: "Receipt printed",
  receipt_downloaded: "Receipt downloaded (PDF)",
};

const FIELD_LABELS = {
  firstName: "First name",
  lastName: "Last name",
  candidateName: "Candidate name",
  candidatePassport: "Passport",
  candidateDob: "Date of birth",
  pinpp: "PINPP",
  phoneNumber: "Phone",
  email: "Email",
  exam: "Exam",
  module: "Module",
  examDate: "Exam date",
  enquiryId: "Enquiry ID",
  transactionId: "Transaction ID",
  amount: "Amount",
  paymentMethod: "Payment method",
  processedBy: "Processed by",
  comments: "Comments",
  paymentNote: "Payment note",
  externalReference: "External reference",
};

const SOURCE_LABELS = {
  pos: "From the POS till",
  "pos-sales": "From POS sales",
  payment: "From this page",
  admin: "Added in the admin panel",
};

const shown = (value) =>
  value === null || value === undefined || value === ""
    ? "(empty)"
    : String(value);

const AuditDetails = ({ entry }) => {
  const meta = entry.meta || {};
  if (entry.action === "updated") {
    return (
      <>
        {(entry.changes || []).map((change) => (
          <div key={change.field}>
            <span className="text-muted">
              {FIELD_LABELS[change.field] || change.field}:
            </span>{" "}
            <del className="text-muted">{shown(change.from)}</del> →{" "}
            {shown(change.to)}
          </div>
        ))}
        {meta.receiptUpdated && (
          <div className="text-small text-muted">
            Receipt updated to match
          </div>
        )}
      </>
    );
  }
  if (entry.action === "created" && meta.source === "pos") {
    return `From the POS till, sale ${meta.saleId || ""}`;
  }
  const parts = [SOURCE_LABELS[meta.source]].filter(Boolean);
  if (meta.receiptRevisedAt) parts.push("corrected receipt");
  return parts.join(" · ") || null;
};

// A saved payment can hold a value the fixed list doesn't offer (an online
// provider, a retired module); keep it selectable so the field isn't blank.
const withCurrent = (options, current) => {
  if (!current) return options;
  const has = options.some(
    (option) => (typeof option === "object" ? option.value : option) === current,
  );
  return has ? options : [...options, current];
};

const PaymentView = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [errorMessage, setErrorMessage] = useState("");
  const [thisId, setThisId] = useState(
    location.pathname.split("/").slice(-1)[0],
  );
  const [isNew, setIsNew] = useState(
    location.pathname.split("/").slice(-1)[0] === "add",
  );
  const [values, setValues] = useState({});
  const [validate, setValidate] = useState({
    emailState: "",
    passportState: "",
  });
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useToken();
  const [examModules, setExamModules] = useState([]);
  const [duplicated, setDuplicated] = useState(false);
  // enquiry ID of the payment a duplicate was copied from
  const [duplicatedFrom, setDuplicatedFrom] = useState("");
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState("");
  // "view" or "regenerate" while the proof document request runs
  const [docBusy, setDocBusy] = useState(null);
  const [regenerateModal, setRegenerateModal] = useState(false);
  const user = useUser();
  const [showForm, setShowForm] = useState(false);
  // the payment as last loaded from the server - what the receipt reflects
  const [savedValues, setSavedValues] = useState(null);
  const [audit, setAudit] = useState([]);
  const [receiptBusy, setReceiptBusy] = useState(null);
  // printing goes through the POS bridge, so it needs POS access
  const canUsePos = Boolean(user?.isSuperadmin || user?.posAccess);

  const [isUser, setIsUser] = useState(user.role === "User" ? true : false);

  // const { id } = useParams();

  const [resending, setResending] = useState(false);

  /** Sends this payment to Smartup again after it was refused. */
  const handleSmartupResend = async () => {
    setResending(true);
    try {
      const { data } = await axios.post(
        `/api/payments/${thisId}/smartup-resend`,
      );
      const result = data?.results?.[0];
      if (result?.ok) {
        toast.success("Smartup accepted the payment");
        setValues((prev) => ({
          ...prev,
          smartupStatus: "sent",
          smartupError: "",
        }));
      } else {
        toast.error(result?.error || "Smartup refused the payment");
        setValues((prev) => ({
          ...prev,
          smartupStatus: "failed",
          smartupError: result?.error || "",
        }));
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Could not resend to Smartup",
      );
    } finally {
      setResending(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`/api/payments/${thisId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDeleteModal(false);
      toast.success("Payment deleted");
      navigate(-1);
    } catch (err) {
      // the modal stays open so the delete can be retried or cancelled
      toast.error(
        err?.response?.data?.message ||
          "The payment was not deleted. Check your connection and try again.",
      );
    } finally {
      setDeleting(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!values.examDate) {
      toast.error("Exam Date is required, please check");
      return;
    } else if (!values.module) {
      toast.error("Exam Module is required, please check");
      return;
    } else if (!values.enquiryId) {
      toast.error("Enquiry ID is required, please check");
      return;
    } else if (!values.amount) {
      if (values.paymentMethod !== "Free Seat") {
        toast.error("Amount is required, please check");
        return;
      }
    }
    if (!saving) {
      setErrorMessage("");
      setSaving(true);
      axios
        .post(
          `/api/payments/${thisId}`,
          {
            ...values,
            firstName: values.firstName ? values.firstName.trim() : null,
            candidatePassport: values.candidatePassport
              ? values.candidatePassport.trim()
              : null,
            lastName: values.lastName ? values.lastName.trim() : "N/A",
            editor: values.editor ? values.editor : user.username,
            editorId: user._id,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        )
        .then((result) => {
          if (result.status === 200) {
            if (duplicated) {
              navigate(-1);
              toast.success("Payment added successfully");
            } else if (isNew) {
              navigate(0);
              toast.success("Payment added successfully");
            } else {
              navigate(-1);
              toast.success("Payment updated");
            }
          }
        })
        .catch((err) => {
          setErrorMessage(
            err?.response?.data?.message ||
              (typeof err?.response?.data === "string"
                ? err.response.data
                : "The payment was not saved. Check your connection and try again."),
          );
        })
        .finally(() => setSaving(false));
    }
  };

  const loadAudit = () =>
    axios
      .get(`/api/payments/${thisId}/audit`)
      .then(({ data }) => setAudit(data.items || []))
      .catch(() => {});

  const logReceiptEvent = (event) =>
    axios
      .post(`/api/payments/${thisId}/receipt-event`, {
        event,
        source: "payment",
      })
      .then(loadAudit)
      .catch(() => {});

  const receiptDirty = Boolean(
    savedValues &&
      RECEIPT_FIELDS.some(
        (field) =>
          String(values[field] ?? "") !== String(savedValues[field] ?? ""),
      ),
  );

  const handleReprint = async () => {
    if (receiptDirty) {
      toast.warn("Save your changes first, so the receipt shows them");
      return;
    }
    setReceiptBusy("print");
    try {
      // the bridge settings live on the server, not in this browser
      await loadBridgeSettings();
      await printReceipt(
        await buildPrintPayload(savedValues.electronicReceipt),
      );
      toast.success("Receipt sent to the printer");
      logReceiptEvent("printed");
    } catch (error) {
      toast.error(`The receipt did not print: ${error.message}`);
    } finally {
      setReceiptBusy(null);
    }
  };

  const handleDownloadPdf = async () => {
    if (receiptDirty) {
      toast.warn("Save your changes first, so the receipt shows them");
      return;
    }
    setReceiptBusy("pdf");
    try {
      await downloadReceiptPdf(
        savedValues.electronicReceipt,
        `receipt-${savedValues.transactionId || savedValues._id}.pdf`,
      );
      logReceiptEvent("downloaded");
    } catch (error) {
      toast.error("Could not create the PDF");
    } finally {
      setReceiptBusy(null);
    }
  };

  const loadPayment = () => {
    setLoading(true);
    setLoadError("");
    axios
      .get(`/api/payments/${thisId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((result) => {
        setValues(result.data);
        setSavedValues(result.data);
        setLoading(false);
        setShowForm(true);
      })
      .catch((err) => {
        setLoading(false);
        const message =
          err?.response?.status === 404
            ? "This payment was not found. It may have been deleted."
            : "Could not load this payment. Check your connection and try again.";
        setLoadError(message);
        toast.error(message);
      });
  };

  useEffect(() => {
    !isNew && loadPayment();
    !isNew && loadAudit();
  }, []);

  const handleValuesChange = (e) => {
    if (e.target.name === "passportId") {
      setValues({
        ...values,
        [e.target.name]: e.target.value.toUpperCase().replace(" ", ""),
      });
    } else {
      setValues({ ...values, [e.target.name]: e.target.value });
    }
    setErrorMessage("");
  };

  const validatePassportId = (e) => {
    let rex = /[A-Z]{2}[0-9]{7}/;
    if (rex.test(e.target.value)) {
      setValidate({ ...validate, passportState: "has-success" });
    } else {
      setValidate({ ...validate, passportState: "has-danger" });
    }
  };

  const handleLookupPinpp = async () => {
    const pinpp = values.pinpp ? String(values.pinpp).trim() : "";
    const rex = /^\d{14}$/;
    if (!rex.test(pinpp)) {
      toast.warn("Please input a valid 14-digit PINPP");
      return;
    }

    const secret = process.env.REACT_APP_PIN_INFO_SECRET;
    if (!secret) {
      toast.error(
        "Missing PIN info secret. Set REACT_APP_PIN_INFO_SECRET and rebuild.",
      );
      return;
    }

    try {
      setLoading(true);
      const result = await axios.post(
        `/api/pin-info`,
        { pinpp },
        {
          headers: {
            "x-pin-info-secret": secret,
          },
        },
      );
      if (result.status === 200) {
        setValues({
          ...values,
          pinpp: result.data?.pinpp ?? pinpp,
          firstName: result.data?.firstName ?? values.firstName,
          lastName: result.data?.lastName ?? values.lastName,
        });
        setShowForm(true);
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 400) {
        toast.error("Invalid PINPP. Please check and try again.");
      } else if (status === 401) {
        toast.error("PIN info secret is missing/invalid.");
      } else if (status === 502) {
        toast.error(
          "PIN info lookup failed (upstream error). Please try again later.",
        );
      } else {
        toast.error("Failed to lookup PINPP. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateDoc = async (rengenerate = false) => {
    if (!values._id || docBusy) return;
    setDocBusy(rengenerate ? "regenerate" : "view");
    try {
      if (values.docNumber && !rengenerate) {
        const fileUrl = `https://payment.edu-action.uk/doc-verification/${values.docNumber}`;
        const win = window.open(fileUrl, "_blank");
        if (win) {
          win.focus();
        }
      } else {
        const result = await axios.post(`/api/docs/create`, {
          enquiryId: values._id,
          candidateName: `${values.firstName} ${values.lastName}`,
          passportNo: values.candidatePassport,
          paymentDate: moment(values.createdAt).format("DD/MM/YYYY"),
          paymentAmount: values.amount,
          examDate: values.examDate,
          signTitle: "Исп. Директор",
          signName: "Усманова М.Б",
        });
        if (result.status === 200 || result.status === 201) {
          setValues({ ...values, docNumber: result.data.number });
          const fileUrl = `https://payment.edu-action.uk/doc-verification/${result.data.number}`;
          const win = window.open(fileUrl, "_blank");
          if (win) {
            win.focus();
          }
        } else {
          toast.error("Failed to generate document, please try again later.");
        }
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          "Could not create the proof document. Check your connection and try again.",
      );
    } finally {
      setDocBusy(null);
    }
  };

  return (
    <AppLayout>
      <div className="dashboard-wrapper">
        <Row>
          <Colxx xxs="12">
            <div className="mb-2 d-flex flex-wrap align-items-start justify-content-between">
              <h1>
                {duplicated
                  ? `New payment (copy of #${duplicatedFrom})`
                  : isNew
                    ? "New payment"
                    : `Payment details #${values.enquiryId || ""}`}
              </h1>
              {!isNew && savedValues?.electronicReceipt && (
                <div className="mb-2">
                  {canUsePos && (
                    <Button
                      color="primary"
                      className="mr-2"
                      disabled={Boolean(receiptBusy)}
                      onClick={handleReprint}
                      title={
                        receiptDirty ? "Save your changes first" : undefined
                      }
                    >
                      <i className="simple-icon-printer mr-1" />
                      {receiptBusy === "print" ? "Printing…" : "Reprint receipt"}
                    </Button>
                  )}
                  <Button
                    outline
                    color="primary"
                    disabled={Boolean(receiptBusy)}
                    onClick={handleDownloadPdf}
                    title={receiptDirty ? "Save your changes first" : undefined}
                  >
                    <i className="simple-icon-cloud-download mr-1" />
                    {receiptBusy === "pdf" ? "Preparing…" : "Download PDF"}
                  </Button>
                </div>
              )}
            </div>
            <Separator className="mb-5" />
          </Colxx>
        </Row>
        {loadError && (
          <LoadError
            message={loadError}
            onRetry={() => {
              loadPayment();
              loadAudit();
            }}
            busy={loading}
          />
        )}
        <div className="loading" hidden={!loading} />
        {!loading && !loadError && (
          <Row>
            <Colxx xxs="12" className="mb-4">
              {duplicated && (
                <Alert color="info" className="text-left">
                  Creating a copy of #{duplicatedFrom}. Nothing is saved until
                  you press Add.
                </Alert>
              )}
              <Card className="mb-4 text-left">
                <CardBody>
                  <Form>
                    {isNew && !showForm && (
                      <Row>
                        <Col md="3">
                          <FormGroup>
                            <Label>PINPP</Label>
                            <Input
                              required
                              type="text"
                              name="pinpp"
                              value={values.pinpp}
                              onChange={handleValuesChange}
                              disabled={
                                user.canEdit ? false : isNew ? false : true
                              }
                            />{" "}
                          </FormGroup>
                        </Col>
                        <Col md="12 d-flex">
                          <Button
                            color="light"
                            onClick={() => setShowForm(true)}
                            className="mr-2"
                            disabled={loading}
                          >
                            Skip
                          </Button>
                          <Button
                            color="primary"
                            onClick={() => handleLookupPinpp()}
                            className="mr-2"
                            disabled={loading}
                          >
                            Next
                          </Button>
                        </Col>
                      </Row>
                    )}
                    {showForm && (
                      <Row>
                        <Col md="12">
                          <Row>
                            <Col md="6">
                              <FormGroup>
                                <Label>First Name</Label>
                                <Input
                                  required
                                  type="text"
                                  name="firstName"
                                  value={values.firstName}
                                  onChange={handleValuesChange}
                                  disabled={
                                    user.canEdit ? false : isNew ? false : true
                                  }
                                />
                              </FormGroup>
                            </Col>
                            <Col md="6">
                              <FormGroup>
                                <Label>Last Name</Label>
                                <Input
                                  required
                                  type="text"
                                  name="lastName"
                                  value={values.lastName}
                                  onChange={handleValuesChange}
                                  disabled={
                                    user.canEdit ? false : isNew ? false : true
                                  }
                                />
                              </FormGroup>
                            </Col>
                            <Col md="6">
                              <FormGroup>
                                <Label>Passport Number</Label>
                                <Input
                                  required
                                  type="text"
                                  name="candidatePassport"
                                  valid={
                                    validate.passportState === "has-success"
                                  }
                                  invalid={
                                    validate.passportState === "has-danger"
                                  }
                                  value={values.candidatePassport}
                                  onChange={(e) => {
                                    validatePassportId(e);
                                    handleValuesChange(e);
                                  }}
                                  disabled={
                                    user.canEdit ? false : isNew ? false : true
                                  }
                                />{" "}
                                <FormFeedback>
                                  Please input a correct passport or ID number.
                                </FormFeedback>
                              </FormGroup>
                            </Col>
                            <Col md="6">
                              <FormGroup>
                                <Label>Exam Date</Label>
                                <Input
                                  required
                                  type="text"
                                  name="examDate"
                                  value={values.examDate}
                                  onChange={handleValuesChange}
                                  disabled={
                                    user.role === "Admin" ? false : !isNew
                                  }
                                />
                              </FormGroup>
                            </Col>
                            <Col md="6">
                              <FormGroup>
                                <Label for="exam">Exam</Label>
                                <SelectInput
                                  inputId="exam"
                                  className="mb-3"
                                  value={values.exam}
                                  name="exam"
                                  options={withCurrent(
                                    ["TKT", "Linguaskill", "IELTS", "Celta", "CEQs"],
                                    values.exam,
                                  )}
                                  onChange={(exam) => {
                                    setValues({
                                      ...values,
                                      exam,
                                    });
                                    let mods = (modules[exam] || []).map(
                                      (mod) => ({
                                        label: mod,
                                        value: mod,
                                      }),
                                    );
                                    setExamModules(mods);
                                  }}
                                  isDisabled={
                                    user.role === "Admin" ? false : !isNew
                                  }
                                />
                              </FormGroup>
                            </Col>
                            <Col md="6">
                              <FormGroup>
                                <Label for="module">Exam Module</Label>
                                <SelectInput
                                  inputId="module"
                                  className="mb-3"
                                  value={values.module}
                                  name="module"
                                  // on a saved payment the module list was
                                  // never filled, so fall back to the exam's
                                  options={withCurrent(
                                    examModules.length
                                      ? examModules
                                      : modules[values.exam] || [],
                                    values.module,
                                  )}
                                  onChange={(module) =>
                                    setValues({
                                      ...values,
                                      module,
                                    })
                                  }
                                  isDisabled={
                                    user.role === "Admin" ? false : !isNew
                                  }
                                />
                              </FormGroup>
                            </Col>
                            <Col md="6">
                              <FormGroup>
                                <Label for="paymentMethod">Payment Method</Label>
                                <SelectInput
                                  inputId="paymentMethod"
                                  className="mb-3"
                                  value={values.paymentMethod}
                                  name="paymentMethod"
                                  options={withCurrent(
                                    [
                                      "Bank",
                                      "Cash",
                                      "Uzcard",
                                      "Humo",
                                      "Free Seat",
                                      "LC",
                                      "Via",
                                      "UPay",
                                      "Click",
                                      "Payme",
                                    ],
                                    values.paymentMethod,
                                  )}
                                  onChange={(paymentMethod) =>
                                    setValues({
                                      ...values,
                                      paymentMethod,
                                    })
                                  }
                                  isDisabled={
                                    user.role === "Admin" ? false : !isNew
                                  }
                                />
                              </FormGroup>
                            </Col>
                            <Col md="6">
                              <FormGroup>
                                <Label>Amount</Label>
                                <Input
                                  required
                                  type="text"
                                  name="amount"
                                  value={values.amount}
                                  onChange={handleValuesChange}
                                  disabled={
                                    user.role === "Admin" ? false : !isNew
                                  }
                                />
                              </FormGroup>
                            </Col>
                            <Col md="6">
                              <FormGroup>
                                <Label>Enquiry ID</Label>
                                <Input
                                  required
                                  type="text"
                                  name="enquiryId"
                                  value={values.enquiryId}
                                  onChange={handleValuesChange}
                                />
                              </FormGroup>
                            </Col>
                            <Col md="6">
                              <FormGroup>
                                <Label>Transaction ID</Label>
                                <Input
                                  required
                                  type="text"
                                  name="transactionId"
                                  value={values.transactionId}
                                  onChange={handleValuesChange}
                                  disabled={
                                    user.canEdit ? false : isNew ? false : true
                                  }
                                />
                              </FormGroup>
                            </Col>
                            <Col md="6">
                              <FormGroup>
                                <Label>PINPP</Label>
                                <Input
                                  required
                                  type="text"
                                  name="pinpp"
                                  value={values.pinpp}
                                  onChange={handleValuesChange}
                                />
                              </FormGroup>
                            </Col>
                            <Col md="6">
                              <FormGroup>
                                <Label>Processed By</Label>
                                <Input
                                  required
                                  type="text"
                                  name="processedBy"
                                  value={values.processedBy}
                                  onChange={handleValuesChange}
                                />
                              </FormGroup>
                            </Col>
                            {!isNew && (
                              <Col md="6">
                                <FormGroup>
                                  <Label>Created At</Label>
                                  <Input
                                    required
                                    type="text"
                                    name="createdAt"
                                    value={moment(values.createdAt).format(
                                      "DD/MM/YYYY",
                                    )}
                                    disabled
                                  />
                                </FormGroup>
                              </Col>
                            )}
                            {!isNew && user.role === "Admin" && (
                              <Col md="6">
                                <FormGroup>
                                  <Label>Editor</Label>
                                  <Input
                                    type="text"
                                    name="editor"
                                    value={values.editor}
                                    disabled
                                  />
                                </FormGroup>
                              </Col>
                            )}
                            <Col md="12">
                              <FormGroup>
                                <Label>Comments</Label>
                                <Input
                                  type="textarea"
                                  name="comments"
                                  value={values.comments}
                                  onChange={handleValuesChange}
                                  disabled={
                                    user.canEdit ? false : isNew ? false : true
                                  }
                                />
                              </FormGroup>
                            </Col>
                            {values.ofd && (
                              <Col md="6">
                                <p>
                                  <b>OFD Details:</b> <br />
                                  <b>Fiscal Sign: </b>
                                  {values.ofd.fiscalSign}
                                  <br />
                                  <b>Receipt ID: </b>
                                  {values.ofd.receiptId}
                                  <br />
                                  <b>Terminal ID: </b>
                                  {values.ofd.terminalId}
                                  <br />
                                  <b>
                                    <a
                                      href={values.ofd.qrCodeUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      QR Code
                                    </a>
                                  </b>
                                  <br />
                                </p>
                              </Col>
                            )}

                            {values.ofdCommission && (
                              <Col md="6">
                                <p>
                                  <b>OFD Commission Details:</b> <br />
                                  <b>Fiscal Sign: </b>
                                  {values.ofdCommission.fiscalSign}
                                  <br />
                                  <b>Receipt ID: </b>
                                  {values.ofdCommission.receiptId}
                                  <br />
                                  <b>Terminal ID: </b>
                                  {values.ofdCommission.terminalId}
                                  <br />
                                  <b>
                                    <a
                                      href={values.ofdCommission.qrCodeUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      QR Code
                                    </a>
                                  </b>
                                  <br />
                                </p>
                              </Col>
                            )}
                          </Row>
                          <p className="text-danger">{errorMessage}</p>
                        </Col>
                        <Col md="12" className="d-flex justify-content-between">
                          <div>
                            <Button
                              color="light"
                              className="top-right-button"
                              onClick={() => navigate(-1)}
                            >
                              Back
                            </Button>

                            {!isNew && (
                              <Button
                                color="light"
                                className="top-right-button ml-2"
                                onClick={() => {
                                  setIsNew(true);
                                  setThisId("add");
                                  let val = { ...values };
                                  delete val._id;
                                  delete val.createdAt;
                                  delete val.updatedAt;
                                  setValues(val);
                                  setDuplicatedFrom(values.enquiryId || "");
                                  setDuplicated(true);
                                }}
                              >
                                Duplicate
                              </Button>
                            )}
                            {!isNew && user.role === "Admin" && (
                              <Button
                                color="danger"
                                className="top-right-button ml-2"
                                onClick={() => setDeleteModal(!deleteModal)}
                              >
                                Delete
                              </Button>
                            )}

                            <Button
                              color="primary"
                              className="top-right-button ml-2"
                              onClick={handleSubmit}
                              disabled={saving}
                            >
                              {saving && (
                                <Spinner size="sm" className="mr-1" />
                              )}
                              {saving ? "Saving…" : isNew ? "Add" : "Update"}
                            </Button>
                          </div>
                          <div>
                            {!isNew && (
                              <span className="align-middle mr-2 text-small">
                                Smartup:{" "}
                                {values.smartupStatus === "failed" ? (
                                  <span
                                    className="text-danger"
                                    title={values.smartupError || ""}
                                  >
                                    failed
                                    {values.smartupAttempts
                                      ? ` (${values.smartupAttempts} tries)`
                                      : ""}
                                  </span>
                                ) : values.smartupStatus === "sent" ||
                                  values.smartupImported ? (
                                  <span className="text-success">sent</span>
                                ) : (
                                  <span
                                    className="text-muted"
                                    title="Taken before the outcome was recorded per payment, so we cannot say either way. Use the bulk import or the Smartup Sync page rather than resending blind."
                                  >
                                    no record
                                  </span>
                                )}
                              </span>
                            )}
                            {/* Only offered where we know Smartup refused it.
                                A payment with no record may well be in Smartup
                                already - pushing it again would double it. */}
                            {!isNew &&
                              user.role === "Admin" &&
                              values.smartupStatus === "failed" && (
                                <Button
                                  color="warning"
                                  className="top-right-button ml-2"
                                  disabled={resending}
                                  onClick={handleSmartupResend}
                                >
                                  {resending
                                    ? "Sending…"
                                    : "Resend to Smartup"}
                                </Button>
                              )}
                            <Button
                              color="info"
                              className="top-right-button ml-2"
                              onClick={() => handleGenerateDoc(false)}
                              disabled={Boolean(docBusy) || !values._id}
                            >
                              {docBusy === "view"
                                ? values.docNumber
                                  ? "Opening…"
                                  : "Generating…"
                                : values.docNumber
                                  ? "View Document"
                                  : "Generate Proof"}
                            </Button>
                            <Button
                              color="info"
                              className="top-right-button ml-2"
                              onClick={() => setRegenerateModal(true)}
                              disabled={Boolean(docBusy) || !values._id}
                              aria-label="Regenerate proof document"
                              title="Regenerate proof document"
                            >
                              {docBusy === "regenerate" ? (
                                <Spinner size="sm" />
                              ) : (
                                <i className="simple-icon-refresh" />
                              )}
                            </Button>
                          </div>
                        </Col>
                      </Row>
                    )}
                  </Form>
                </CardBody>
              </Card>

              {!isNew && (
                <Card className="mb-4 text-left">
                  <CardBody>
                    <h5 className="mb-3">History</h5>
                    {audit.length === 0 ? (
                      <p className="text-muted mb-0">
                        Nothing recorded yet. Edits, deletions and receipt
                        prints and downloads are recorded from now on.
                      </p>
                    ) : (
                      <div className="table-responsive">
                        <table className="table table-sm mb-0">
                          <thead>
                            <tr>
                              <th>When (GMT+5)</th>
                              <th>Who</th>
                              <th>What</th>
                              <th>Details</th>
                            </tr>
                          </thead>
                          <tbody>
                            {audit.map((entry) => (
                              <tr key={entry._id}>
                                <td className="text-nowrap">
                                  {moment(entry.createdAt)
                                    .utcOffset("+05:00")
                                    .format("DD/MM/YYYY HH:mm")}
                                </td>
                                <td>{entry.actor?.username || "—"}</td>
                                <td className="text-nowrap">
                                  {AUDIT_LABELS[entry.action] || entry.action}
                                </td>
                                <td>
                                  <AuditDetails entry={entry} />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </CardBody>
                </Card>
              )}
            </Colxx>
          </Row>
        )}
        {deleteModal && (
          <ConfirmModal
            modal={deleteModal}
            setModal={setDeleteModal}
            title={"Delete Payment"}
            body={`Delete the payment${
              savedValues?.firstName || savedValues?.lastName
                ? ` for ${[savedValues.firstName, savedValues.lastName]
                    .filter(Boolean)
                    .join(" ")}`
                : ""
            }${
              savedValues?.enquiryId ? ` (#${savedValues.enquiryId})` : ""
            }? This cannot be undone.`}
            action={handleDelete}
            confirmLabel={deleting ? "Deleting…" : "Delete payment"}
            cancelLabel="Keep payment"
            confirmColor="danger"
            busy={deleting}
            closeOnConfirm={false}
          />
        )}
        {regenerateModal && (
          <ConfirmModal
            modal={regenerateModal}
            setModal={setRegenerateModal}
            title="Regenerate proof document?"
            body="A new proof document is created from the payment as it is now and opened in a new tab."
            action={() => handleGenerateDoc(true)}
            confirmLabel="Regenerate document"
          />
        )}
      </div>
    </AppLayout>
  );
};

export default PaymentView;
